import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {
  Plus,
  Search,
  LayoutGrid,
  Rows3,
  Copy,
  Trash2,
  ImagePlus,
  ArrowUpRight,
  X,
} from "lucide-react";
import { api, json } from "./api";
import { Modal, Empty } from "./components";
import { useResource } from "./useResource";
import { ResourceStatus } from "./ResourceStatus";
import { Backlinks } from "./Backlinks";
import type { EditorHandle } from "./DocumentEditor";
import type {
  Asset,
  Backlink,
  Category,
  DocumentMeta,
  Entity,
  EntityProperty,
  Reference,
} from "./types";

type Props = {
  projectId: string;
  entities: Entity[];
  categories: Category[];
  assets: Asset[];
  documents: DocumentMeta[];
  readOnly: boolean;
  initialId?: string | null;
  onSaved: (e: Entity) => void;
  onReload: () => Promise<void>;
  onOpen: (r: Backlink) => void;
  onAsset: (a: Asset) => void;
  onSelection: (id: string | null) => void;
};
type PanelProps = Props & {
  initial: Entity;
  onSelect: (e: Entity) => void;
  view: string;
  filter: string;
  search: string;
};
const typeLabels = {
  text: "Текст",
  number: "Число",
  boolean: "Переключатель",
  image: "Изображение",
  reference: "Ссылка на материал",
};
function PropertyControl({
  property,
  assets,
  entities,
  documents,
  disabled,
  onChange,
}: {
  property: EntityProperty;
  assets: Asset[];
  entities: Entity[];
  documents: DocumentMeta[];
  disabled: boolean;
  onChange: (value: EntityProperty["value"]) => void;
}) {
  const p = property,
    label = p.name;
  if (p.type === "boolean")
    return (
      <input
        aria-label={label}
        disabled={disabled}
        type="checkbox"
        checked={p.value === true}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  if (p.type === "image")
    return (
      <select
        aria-label={label}
        disabled={disabled}
        value={String(p.value || "")}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">Не выбрано</option>
        {assets.map((a) => (
          <option value={a.id} key={a.id}>
            {a.name}
          </option>
        ))}
      </select>
    );
  if (p.type === "reference") {
    const value = p.value as Reference | null;
    return (
      <select
        aria-label={label}
        disabled={disabled}
        value={value ? value.type + ":" + value.id : ""}
        onChange={(e) => {
          const [type, id] = e.target.value.split(":");
          onChange(id ? { type: type as Reference["type"], id } : null);
        }}
      >
        <option value="">Не выбрано</option>
        <optgroup label="Документы">
          {documents.map((d) => (
            <option key={d.id} value={"document:" + d.id}>
              {d.title}
            </option>
          ))}
        </optgroup>
        <optgroup label="Сущности">
          {entities.map((e) => (
            <option key={e.id} value={"entity:" + e.id}>
              {e.title}
            </option>
          ))}
        </optgroup>
        <optgroup label="Изображения">
          {assets.map((a) => (
            <option key={a.id} value={"asset:" + a.id}>
              {a.name}
            </option>
          ))}
        </optgroup>
      </select>
    );
  }
  return (
    <input
      aria-label={label}
      disabled={disabled}
      type={p.type === "number" ? "number" : "text"}
      step={p.type === "number" ? "any" : undefined}
      maxLength={p.type === "text" ? 4000 : undefined}
      value={String(p.value ?? "")}
      onChange={(e) => {
        const v = p.type === "number" ? Number(e.target.value) : e.target.value;
        if (typeof v !== "number" || Number.isFinite(v)) onChange(v);
      }}
    />
  );
}
const EntityPanel = forwardRef<EditorHandle, PanelProps>(
  function EntityPanel(props, ref) {
    const {
      initial,
      projectId,
      onSaved,
      readOnly,
      assets,
      categories,
      documents,
      entities,
      view,
      filter,
      search,
    } = props;
    const resource = useResource(
        initial,
        `/projects/${projectId}/entities/${initial.id}`,
        onSaved,
      ),
      { data, change, flush } = resource;
    const [fieldModal, setFieldModal] = useState(false),
      [fieldName, setFieldName] = useState(""),
      [fieldType, setFieldType] = useState<EntityProperty["type"]>("number"),
      [fieldError, setFieldError] = useState(""),
      [trash, setTrash] = useState(false),
      [error, setError] = useState(""),
      [busy, setBusy] = useState(false);
    const imageInput = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => ({ flush }), [flush]);
    const rows = entities
      .map((e) => (e.id === data.id ? data : e))
      .filter(
        (e) =>
          (!filter || e.categoryId === filter) &&
          (!search ||
            `${e.title} ${e.description}`
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase())),
      );
    const columns = [
      ...new Map(
        rows
          .flatMap((e) => e.properties)
          .map((p) => [p.name + ":" + p.type, p]),
      ).values(),
    ];
    const update = (id: string, value: EntityProperty["value"]) =>
      change({
        properties: data.properties.map((p) =>
          p.id === id ? { ...p, value } : p,
        ),
      });
    async function duplicate() {
      setBusy(true);
      try {
        if (!(await flush())) return;
        const result = await api<Entity>(
          `/projects/${projectId}/entities`,
          json("POST", {
            ...resource.getCurrent(),
            title: resource.getCurrent().title.slice(0, 150) + " — копия",
          }),
        );
        await props.onReload();
        props.onSelect(result);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    }
    return (
      <>
        <div className="entity-layout">
          <div className="entity-collection">
            {rows.length ? (
              view === "cards" ? (
                <div className="entity-grid">
                  {rows.map((e) => (
                    <button
                      className={`entity-card ${e.id === data.id ? "selected" : ""}`}
                      key={e.id}
                      onClick={() => props.onSelect(e)}
                      aria-pressed={e.id === data.id}
                    >
                      {e.imageId ? (
                        <img
                          src={`/api/projects/${projectId}/assets/${e.imageId}`}
                          alt=""
                        />
                      ) : (
                        <div className="entity-visual">
                          <span>{e.title.slice(0, 1).toUpperCase()}</span>
                        </div>
                      )}
                      <div>
                        <span className="eyebrow">
                          {categories.find((c) => c.id === e.categoryId)?.title}
                        </span>
                        <h2>{e.title}</h2>
                        <p>{e.description || "Добавь описание и параметры"}</p>
                        <div className="entity-summary">
                          {e.properties.slice(0, 3).map((p) => (
                            <span key={p.id}>
                              {p.name}:{" "}
                              {p.type === "boolean"
                                ? p.value
                                  ? "Да"
                                  : "Нет"
                                : typeof p.value === "object"
                                  ? "↗"
                                  : p.type === "image"
                                    ? "Изображение"
                                    : String(p.value)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="entity-table-wrap">
                  <table className="entity-table">
                    <thead>
                      <tr>
                        <th>Сущность</th>
                        <th>Категория</th>
                        {columns.map((p) => (
                          <th key={p.name + p.type}>{p.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((e) => (
                        <tr
                          key={e.id}
                          className={e.id === data.id ? "selected" : ""}
                        >
                          <td>
                            <button onClick={() => props.onSelect(e)}>
                              {e.title}
                              <ArrowUpRight size={13} />
                            </button>
                          </td>
                          <td>
                            {
                              categories.find((c) => c.id === e.categoryId)
                                ?.title
                            }
                          </td>
                          {columns.map((column) => {
                            const p = e.properties.find(
                              (p) =>
                                p.name === column.name &&
                                p.type === column.type,
                            );
                            return (
                              <td key={column.name + column.type}>
                                {p ? (
                                  <PropertyControl
                                    property={p}
                                    assets={assets}
                                    entities={entities}
                                    documents={documents}
                                    disabled={readOnly || e.id !== data.id}
                                    onChange={(value) => update(p.id, value)}
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="muted small-text">
                    Выбери строку по названию, чтобы редактировать её параметры.
                    Остальные строки откроются для правок после выбора.
                  </p>
                </div>
              )
            ) : (
              <Empty title="Ничего не найдено">
                Попробуй другое название или категорию.
              </Empty>
            )}
          </div>
          <aside className="entity-inspector">
            <ResourceStatus resource={resource} readOnly={readOnly} />
            <div className="entity-inspector-actions">
              <button
                className="text-button"
                disabled={readOnly || busy}
                onClick={() => void duplicate()}
              >
                <Copy size={14} />
                Дублировать
              </button>
              <button
                className="icon-button"
                aria-label="Удалить сущность"
                disabled={readOnly}
                onClick={() => setTrash(true)}
              >
                <Trash2 size={15} />
              </button>
            </div>
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}
            <label>
              Название сущности
              <input
                aria-label="Название сущности"
                value={data.title}
                maxLength={160}
                disabled={readOnly}
                onChange={(e) => change({ title: e.target.value })}
              />
            </label>
            <label>
              Категория
              <select
                value={data.categoryId}
                disabled={readOnly}
                onChange={(e) => change({ categoryId: e.target.value })}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Описание
              <textarea
                value={data.description}
                rows={3}
                maxLength={10000}
                disabled={readOnly}
                onChange={(e) => change({ description: e.target.value })}
              />
            </label>
            <label>
              Изображение сущности
              <select
                value={data.imageId || ""}
                disabled={readOnly}
                onChange={(e) => change({ imageId: e.target.value || null })}
              >
                <option value="">Без изображения</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="text-button"
              disabled={readOnly || busy}
              onClick={() => imageInput.current?.click()}
            >
              <ImagePlus size={14} />
              Загрузить изображение
            </button>
            <input
              ref={imageInput}
              type="file"
              className="file-input"
              accept="image/png,image/jpeg,image/webp"
              aria-label="Изображение для сущности"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setBusy(true);
                try {
                  const body = new FormData();
                  body.append("image", file);
                  const asset = await api<Asset>(
                    `/projects/${projectId}/assets`,
                    { method: "POST", body },
                  );
                  props.onAsset(asset);
                  change({ imageId: asset.id });
                } catch (err) {
                  setError((err as Error).message);
                } finally {
                  setBusy(false);
                  if (imageInput.current) imageInput.current.value = "";
                }
              }}
            />
            <div className="entity-fields-heading">
              <span className="eyebrow">ПАРАМЕТРЫ</span>
              <button
                className="text-button"
                disabled={readOnly}
                onClick={() => {
                  setFieldName("");
                  setFieldError("");
                  setFieldModal(true);
                }}
              >
                <Plus size={13} />
                Поле
              </button>
            </div>
            {!data.properties.length && (
              <p className="muted small-text">
                Добавь скорость, стоимость, здоровье или другие характеристики.
              </p>
            )}
            {data.properties.map((p) => (
              <div className="entity-property" key={p.id}>
                <div>
                  <span>
                    {p.name}
                    <small>{typeLabels[p.type]}</small>
                  </span>
                  <button
                    className="icon-button"
                    aria-label={`Удалить поле ${p.name}`}
                    disabled={readOnly}
                    onClick={() =>
                      change({
                        properties: data.properties.filter(
                          (f) => f.id !== p.id,
                        ),
                      })
                    }
                  >
                    <X size={13} />
                  </button>
                </div>
                <PropertyControl
                  property={p}
                  assets={assets}
                  entities={entities}
                  documents={documents}
                  disabled={readOnly}
                  onChange={(value) => update(p.id, value)}
                />
                {p.type === "reference" &&
                  p.value &&
                  typeof p.value === "object" &&
                  p.value.type !== "asset" && (
                    <button
                      className="text-button"
                      onClick={() =>
                        props.onOpen({
                          ...(p.value as Reference),
                          type: (p.value as Reference).type as
                            "entity" | "document",
                          title: p.name,
                        })
                      }
                    >
                      Открыть материал <ArrowUpRight size={12} />
                    </button>
                  )}
              </div>
            ))}
            <Backlinks
              projectId={projectId}
              type="entity"
              id={data.id}
              version={data.version}
              onOpen={props.onOpen}
            />
          </aside>
        </div>
        {fieldModal && (
          <Modal title="Добавить параметр" close={() => setFieldModal(false)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const name = fieldName.trim();
                if (
                  data.properties.some(
                    (p) =>
                      p.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
                  )
                ) {
                  setFieldError("Поле с таким названием уже есть.");
                  return;
                }
                change({
                  properties: [
                    ...data.properties,
                    {
                      id: crypto.randomUUID(),
                      name,
                      type: fieldType,
                      value:
                        fieldType === "boolean"
                          ? false
                          : fieldType === "number"
                            ? 0
                            : fieldType === "text"
                              ? ""
                              : null,
                    },
                  ],
                });
                setFieldModal(false);
              }}
            >
              <label>
                Название поля
                <input
                  autoFocus
                  required
                  maxLength={80}
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  placeholder="Например, Скорость"
                />
              </label>
              <label>
                Тип поля
                <select
                  value={fieldType}
                  onChange={(e) =>
                    setFieldType(e.target.value as EntityProperty["type"])
                  }
                >
                  {Object.entries(typeLabels).map(([id, title]) => (
                    <option key={id} value={id}>
                      {title}
                    </option>
                  ))}
                </select>
              </label>
              {fieldError && (
                <div className="error-message" role="alert">
                  {fieldError}
                </div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setFieldModal(false)}
                >
                  Отмена
                </button>
                <button className="primary">Добавить поле</button>
              </div>
            </form>
          </Modal>
        )}
        {trash && (
          <Modal
            title="Переместить сущность в корзину?"
            close={() => setTrash(false)}
          >
            <p>
              Карточки на досках сохранят ссылку. После восстановления сущность
              снова станет доступна.
            </p>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setTrash(false)}>
                Отмена
              </button>
              <button
                className="danger"
                onClick={async () => {
                  try {
                    if (!(await flush())) return;
                    await api(
                      `/projects/${projectId}/entities/${data.id}/trash`,
                      json("POST", { version: resource.getCurrent().version }),
                    );
                    setTrash(false);
                    props.onSelection(null);
                    await props.onReload();
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                В корзину
              </button>
            </div>
          </Modal>
        )}
      </>
    );
  },
);
export const Entities = forwardRef<EditorHandle, Props>(
  function Entities(props, ref) {
    const { entities, categories, projectId, readOnly } = props;
    const [selected, setSelected] = useState<string | null>(
        props.initialId || entities[0]?.id || null,
      ),
      [view, setView] = useState("cards"),
      [filter, setFilter] = useState(""),
      [search, setSearch] = useState(""),
      [create, setCreate] = useState(false),
      [newCategory, setNewCategory] = useState(false),
      [name, setName] = useState(""),
      [category, setCategory] = useState(categories[0]?.id || ""),
      [busy, setBusy] = useState(false),
      [error, setError] = useState("");
    const panel = useRef<EditorHandle>(null),
      initial = entities.find((e) => e.id === selected) || entities[0];
    useImperativeHandle(
      ref,
      () => ({
        flush: async () => !panel.current || (await panel.current.flush()),
      }),
      [],
    );
    async function select(e: Entity) {
      if (panel.current && !(await panel.current.flush())) return;
      setSelected(e.id);
      props.onSelection(e.id);
    }
    return (
      <div className="entities-page">
        <div className="design-heading">
          <div>
            <span className="eyebrow">ИГРОВЫЕ ДАННЫЕ</span>
            <h1>Сущности</h1>
            <p className="muted">
              Персонажи, предметы и параметры будущей игры.
            </p>
          </div>
          <button
            className="primary"
            disabled={readOnly}
            onClick={() => {
              setName("");
              setError("");
              setCreate(true);
            }}
          >
            <Plus size={16} />
            Новая сущность
          </button>
        </div>
        <div className="design-filters">
          <label className="search-field">
            <Search size={15} />
            <input
              aria-label="Поиск сущностей"
              placeholder="Найти сущность…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <select
            aria-label="Фильтр категории"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">Все категории</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <button
            className="text-button"
            disabled={readOnly}
            onClick={() => {
              setName("");
              setError("");
              setNewCategory(true);
            }}
          >
            <Plus size={14} />
            Категория
          </button>
          <div className="segmented">
            <button
              aria-label="Карточки сущностей"
              aria-pressed={view === "cards"}
              onClick={() => setView("cards")}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              aria-label="Таблица сущностей"
              aria-pressed={view === "table"}
              onClick={() => setView("table")}
            >
              <Rows3 size={15} />
            </button>
          </div>
        </div>
        {initial ? (
          <EntityPanel
            key={initial.id}
            {...props}
            initial={initial}
            ref={panel}
            view={view}
            filter={filter}
            search={search}
            onSelect={(e) => void select(e)}
            onSelection={(id) => {
              setSelected(id);
              props.onSelection(id);
            }}
          />
        ) : (
          <Empty
            title="Создай первую сущность"
            action={
              <button
                className="primary"
                disabled={readOnly}
                onClick={() => {
                  setName("");
                  setCreate(true);
                }}
              >
                <Plus size={16} />
                Создать сущность
              </button>
            }
          >
            Определи характеристики игрока, предмета или объекта окружения.
          </Empty>
        )}
        {(create || newCategory) && (
          <Modal
            title={newCategory ? "Новая категория" : "Новая сущность"}
            close={() => {
              setCreate(false);
              setNewCategory(false);
            }}
          >
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setBusy(true);
                setError("");
                try {
                  if (panel.current && !(await panel.current.flush())) return;
                  if (newCategory) {
                    await api(
                      `/projects/${projectId}/categories`,
                      json("POST", { title: name }),
                    );
                    await props.onReload();
                  } else {
                    const result = await api<Entity>(
                      `/projects/${projectId}/entities`,
                      json("POST", { title: name, categoryId: category }),
                    );
                    await props.onReload();
                    setSelected(result.id);
                    props.onSelection(result.id);
                  }
                  setCreate(false);
                  setNewCategory(false);
                } catch (err) {
                  setError((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label>
                Название
                <input
                  autoFocus
                  required
                  maxLength={160}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    newCategory ? "Например, Способности" : "Например, Игрок"
                  }
                />
              </label>
              {!newCategory && (
                <label>
                  Категория
                  <select
                    value={category}
                    required
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {error && (
                <div className="error-message" role="alert">
                  {error}
                </div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setCreate(false);
                    setNewCategory(false);
                  }}
                >
                  Отмена
                </button>
                <button className="primary" disabled={busy}>
                  {newCategory ? "Создать категорию" : "Создать сущность"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    );
  },
);
